import { useCallback, useState, useRef, useEffect } from "react";
import {
  useUpdateSprintMutation,
  useGetSprintByIdQuery,
} from "@/features/bughurd/api";

export const useRetrospectiveData = (
  sprintId: string,
  templateId: string,
  templateName: string,
  nodes: any[],
  edges: any[],
  reactFlowInstance: any,
  userId: string,
  prepareTemplateDataCallback: () => any[]
) => {
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState<Date | null>(null);
  const [saveStatus, setSaveStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const [hasPendingSave, setHasPendingSave] = useState(false);

  const updateSprintTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const retroStartDate = new Date().toISOString().split("T")[0];

  const { data: sprintDetails, isLoading: isLoadingSprintDetails } =
    useGetSprintByIdQuery({ sprintId }, { skip: !sprintId });
  const [updateSprint, { isLoading: isUpdatingSprint }] =
    useUpdateSprintMutation();

  const markUserChange = useCallback(() => setHasPendingSave(true), []);

  const saveFlowData = useCallback(async () => {
    if (updateSprintTimeoutRef.current) {
      clearTimeout(updateSprintTimeoutRef.current);
    }

    updateSprintTimeoutRef.current = setTimeout(async () => {
      if (nodes.length === 0 && edges.length === 0) return;
      if (!hasPendingSave) return;

      setHasPendingSave(false);
      setSaveStatus("saving");

      try {
        const payload = {
          sprintId,
          isRetro: true,
          retro: [
            {
              startDate: retroStartDate,
              endDate: null,
              templateName,
              templateId,
              templateData: prepareTemplateDataCallback(),
            },
          ],
        };

        await updateSprint(payload).unwrap();
        setLastSaveTime(new Date());
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus("idle"), 2000);
      } catch (error) {
        console.error("Error saving retrospective data:", error);
        setSaveStatus("error");
        setHasPendingSave(true);
        setTimeout(() => setSaveStatus("idle"), 5000);
      }
    }, 1000);
  }, [
    sprintId,
    templateId,
    templateName,
    retroStartDate,
    prepareTemplateDataCallback,
    updateSprint,
    nodes,
    edges,
    hasPendingSave,
  ]);

  const loadExistingData = useCallback(
    (
      setNodes: (nodes: any) => void,
      setEdges: (edges: any) => void,
      createDefaultNodes?: () => any[],
      formatLoadedNodes?: (nodes: any[]) => any[],
      formatLoadedEdges?: (edges: any[]) => any[]
    ) => {
      if (!sprintDetails?.data?.decryptedData) {
        if (createDefaultNodes) {
          const defaultNodes = createDefaultNodes();
          setNodes(defaultNodes);
        }
        setIsDataLoaded(true);
        return;
      }

      const decryptedData = sprintDetails.data.decryptedData;
      if (
        !decryptedData.retro ||
        !Array.isArray(decryptedData.retro) ||
        decryptedData.retro.length === 0
      ) {
        if (createDefaultNodes) {
          const defaultNodes = createDefaultNodes();
          setNodes(defaultNodes);
        }
        setIsDataLoaded(true);
        return;
      }

      const retroData = decryptedData.retro.find(
        (r) => r.templateId === templateId
      );
      if (!retroData || !retroData.templateData) {
        if (createDefaultNodes) {
          const defaultNodes = createDefaultNodes();
          setNodes(defaultNodes);
        }
        setIsDataLoaded(true);
        return;
      }

      const templateData = retroData.templateData[0];
      if (!templateData || !templateData.flowData) {
        if (createDefaultNodes) {
          const defaultNodes = createDefaultNodes();
          setNodes(defaultNodes);
        }
        setIsDataLoaded(true);
        return;
      }

      const { flowData } = templateData; // Remove canvasMetadata destructuring
      const { nodes: loadedNodes, edges: loadedEdges } = flowData;

      if (Array.isArray(loadedNodes)) {
        const formattedNodes = formatLoadedNodes
          ? formatLoadedNodes(loadedNodes)
          : loadedNodes.map((node: any) => ({
              id: node.id,
              type: node.type || "resizableNote",
              position: node.position || { x: 0, y: 0 },
              data: {
                ...node.data,
                reactions: node.data.reactions || [],
                comments: node.data.comments || [],
              },
              style: node.style || {},
            }));
        setNodes(formattedNodes);
      } else if (createDefaultNodes) {
        const defaultNodes = createDefaultNodes();
        setNodes(defaultNodes);
      }

      if (Array.isArray(loadedEdges)) {
        const formattedEdges = formatLoadedEdges
          ? formatLoadedEdges(loadedEdges)
          : loadedEdges.map((edge: any) => ({
              id: edge.id,
              source: edge.source,
              target: edge.target,
              sourceHandle: edge.sourceHandle,
              targetHandle: edge.targetHandle,
              type: edge.type || "deleteable",
              style: edge.style || {},
              markerEnd: edge.markerEnd,
            }));
        setEdges(formattedEdges);
      }

      // Always fit view after loading data, regardless of saved viewport
      if (reactFlowInstance) {
        setTimeout(() => {
          reactFlowInstance.fitView({
            padding: 0.2,
            duration: 800,
            includeHiddenNodes: false,
          });
        }, 100);
      }

      setIsDataLoaded(true);
    },
    [sprintDetails, templateId, reactFlowInstance]
  );

  useEffect(() => {
    return () => {
      if (updateSprintTimeoutRef.current) {
        clearTimeout(updateSprintTimeoutRef.current);
      }
    };
  }, []);

  return {
    isDataLoaded,
    setIsDataLoaded,
    lastSaveTime,
    saveStatus,
    setSaveStatus,
    hasPendingSave,
    markUserChange,
    saveFlowData,
    loadExistingData,
    isLoadingSprintDetails,
    isUpdatingSprint,
    sprintDetails,
  };
};
